import { EnvironmentProviders, importProvidersFrom, inject, LOCALE_ID, makeEnvironmentProviders, provideEnvironmentInitializer } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';

import { DateTimeAdapter, OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE, OwlDateTimeIntl, OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { NgSelectModule } from '@ng-select/ng-select';
import { nl } from 'date-fns/locale';

import { BaasI18n, LOCALE } from '@lvflanders/bar';
import { provideBaasErrorHandler } from '@lvflanders/bar-ng';

import { _BaasDateTimePickerNoopProvider, BAAS_DATE_TIME_PICKER_PROVIDER } from '../date-time/baas-date-time-picker/baas-date-time-picker.provider';
import {
    BAR_DEFAULT_DATE_TIME_FORMATS,
    BarDateFnsDateTimeAdapter,
    OWL_DATEFNS_DATE_TIME_ADAPTER_OPTIONS,
    OwlDateFnsDateTimeAdapterOptions
} from '../date-time/bar-date-fns-date-time-adapter';
import { BarDateTimeIntl } from '../date-time/bar-date-time-intl';
import { BaasResizeObserverErrorHandlerHook } from '../error-handling/baas-resize-observer-error-handler.hook';
import { provideBaasToastPortAdapter } from '../toast-messages/baas-toast-port-adapter-provider';
import { BarControlsFeature } from './bar-controls.feature';
import { BAR_CONTROLS, GENERAL_UI_I18N } from './bar-controls.i18n';

function dateTimeLocaleFactory(locale: string): () => string {
    return () => locale;
}

export const BAR_DATE_TIME_PROVIDERS = [
    { provide: OwlDateTimeIntl, useClass: BarDateTimeIntl },
    { provide: OWL_DATE_TIME_LOCALE, useFactory: dateTimeLocaleFactory, deps: [LOCALE_ID] },
    { provide: DateTimeAdapter, useClass: BarDateFnsDateTimeAdapter },
    { provide: OWL_DATE_TIME_FORMATS, useValue: BAR_DEFAULT_DATE_TIME_FORMATS },
    { provide: OWL_DATEFNS_DATE_TIME_ADAPTER_OPTIONS, useValue: <OwlDateFnsDateTimeAdapterOptions>{ locale: nl, weekStartsOn: 1 } }
];

export function provideBarControls(...features: BarControlsFeature[]): EnvironmentProviders {
    return makeEnvironmentProviders([
        // NOTE needed for MatDialog in BaasConfirmService
        importProvidersFrom([MatDialogModule, MatMenuModule]),
        // NOTE needed for BarSelectList
        importProvidersFrom(NgSelectModule),
        // NOTE needed for DateTimePicker
        importProvidersFrom(OwlDateTimeModule),
        provideEnvironmentInitializer(() => {
            const i18n = inject(BaasI18n);
            i18n.addTranslations('DOMAIN_BAR_CONTROLS', BAR_CONTROLS);
            i18n.addTranslations('GENERAL', GENERAL_UI_I18N);
        }),
        ...BAR_DATE_TIME_PROVIDERS,
        // float label always
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
        // use Flanders culture
        { provide: MAT_DATE_LOCALE, useValue: LOCALE.FLEMISH },
        {
            provide: BAAS_DATE_TIME_PICKER_PROVIDER,
            useValue: _BaasDateTimePickerNoopProvider
        },
        provideBaasErrorHandler(BaasResizeObserverErrorHandlerHook),
        // Register BaasToastPort adapter in inversify container (if available)
        provideBaasToastPortAdapter(),
        // Providing services for the features
        features?.map((f) => f.providers)
    ]);
}
